import { useEffect, useRef } from 'react';
import { KeenIcon } from '@/components';
import { HarborDeletionRequest } from '@/types/harbor-management.types';
import clsx from 'clsx';
import { formatDisplayDateTime } from '@/utils/Date';
import { formatInternationalPhoneNumber } from '@/utils';

interface HarborDeletionViewModalProps {
  isOpen: boolean;
  request: HarborDeletionRequest;
  onClose: () => void;
}

export const HarborDeletionViewModal = ({
  isOpen,
  request,
  onClose,
}: HarborDeletionViewModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      const timer = setTimeout(() => {
        modalRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  return (
    <div
      ref={modalRef}
      tabIndex={-1}
      autoFocus
      className="w-full max-w-2xl bg-gray-100 rounded-2xl p-6 shadow-xl mx-auto relative"
      aria-labelledby="deletion-view-title"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
        aria-label="Close modal"
      >
        <KeenIcon type="filled" icon="close" className="h-6 w-6" />
      </button>

      <h1
        id="deletion-view-title"
        className="text-lg font-medium leading-6 text-gray-900 border-b pb-4"
      >
        Harbor Deletion Request Details
      </h1>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="form-control sm:col-span-2">
          <label htmlFor="harbor-name-view" className="label">
            <span className="label-text font-medium">Harbor</span>
          </label>
          <input
            id="harbor-name-view"
            className={clsx('input input-bordered w-full bg-light-active')}
            value={request.name}
            readOnly
          />
        </div>

        <div className="form-control sm:col-span-2">
          <label htmlFor="harbor-location-view" className="label">
            <span className="label-text font-medium">Location</span>
          </label>
          <input
            id="harbor-location-view"
            className={clsx('input input-bordered w-full bg-light-active')}
            value={request.location || '-'}
            readOnly
          />
        </div>

        <div className="form-control">
          <span className="label-text font-medium">Status</span>
          <div className="flex items-center h-12 px-4 border border-gray-300 rounded-lg bg-light-active">
            <span className="badge badge-warning">Pending Deletion</span>
          </div>
        </div>

        <div className="form-control">
          <label htmlFor="requested-date-view" className="label">
            <span className="label-text font-medium">Requested Date</span>
          </label>
          <input
            id="requested-date-view"
            className={clsx('input input-bordered w-full bg-light-active')}
            value={formatDisplayDateTime(
              new Date(request.deletion_requested_at)
            )}
            readOnly
          />
        </div>

        <div className="form-control">
          <label htmlFor="requested-by-view" className="label">
            <span className="label-text font-medium">Requested By</span>
          </label>
          <input
            id="requested-by-view"
            className={clsx('input input-bordered w-full bg-light-active')}
            value={request.deletion_requested_by?.name || '-'}
            readOnly
          />
        </div>

        <div className="form-control">
          <label htmlFor="requested-by-email-view" className="label">
            <span className="label-text font-medium">Requested By Email</span>
          </label>
          <input
            id="requested-by-email-view"
            className={clsx('input input-bordered w-full bg-light-active')}
            value={request.deletion_requested_by?.email || '-'}
            readOnly
          />
        </div>

        <div className="form-control">
          <label htmlFor="contact-email-view" className="label">
            <span className="label-text font-medium">Contact Email</span>
          </label>
          <input
            id="contact-email-view"
            className={clsx('input input-bordered w-full bg-light-active')}
            value={request.contact_email}
            readOnly
          />
        </div>

        <div className="form-control">
          <label htmlFor="contact-phone-view" className="label">
            <span className="label-text font-medium">Contact Phone</span>
          </label>
          <input
            id="contact-phone-view"
            className={clsx('input input-bordered w-full bg-light-active')}
            value={
              request.contact_phone
                ? formatInternationalPhoneNumber(
                    request.contact_phone,
                    request.country_code,
                    (request as unknown as Record<string, unknown>)
                      .country_short_code as string | undefined
                  )
                : '-'
            }
            readOnly
          />
        </div>

        {request.deletion_reason && (
          <div className="form-control sm:col-span-2">
            <label htmlFor="deletion-reason-view" className="label">
              <span className="label-text font-medium">
                Reason for Deletion
              </span>
            </label>
            <textarea
              id="deletion-reason-view"
              className={clsx(
                'textarea textarea-bordered w-full bg-light-active'
              )}
              value={request.deletion_reason}
              rows={4}
              readOnly
            />
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          className="btn btn-sm btn-light"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};
